import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";

const sessionCache = {};

export default function useTestSession(testId, userUid) {
  const [sessionType, setSessionType] = useState(null); // "assignment" | "diagnostic"
  const [definition, setDefinition] = useState(null);   // packet OR diagnostic
  const [progress, setProgress] = useState(null);       // assignment OR diagnosticAttempt
  const [questions, setQuestions] = useState([]);       // ✅ ADD THIS
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!testId || !userUid) return;

    // ✅ Serve from cache
    if (sessionCache[testId]) {
      const cached = sessionCache[testId];
      setSessionType(cached.sessionType);
      setDefinition(cached.definition);
      setProgress(cached.progress);
      setQuestions(cached.questions);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);

        /* ======================
           ASSIGNMENT FLOW
        ====================== */

        const assignmentRef = doc(db, "users", userUid, "assignments", testId);
        const assignmentSnap = await getDoc(assignmentRef);

        const packetRef = doc(db, "packets", testId);
        const packetSnap = await getDoc(packetRef);

        if (assignmentSnap.exists() && packetSnap.exists()) {
          const packetData = packetSnap.data();

          // 🔑 Load all questions
          const questionDocs = await Promise.all(
            (packetData.questionIds || []).map((qid) =>
              getDoc(doc(db, "questions", qid))
            )
          );

          const loadedQuestions = questionDocs
            .filter((snap) => snap.exists())
            .map((snap) => ({
              id: snap.id,
              ...snap.data(),
            }));

          const session = {
            sessionType: "assignment",
            definition: { id: packetSnap.id, ...packetData },
            progress: { id: assignmentSnap.id, ...assignmentSnap.data() },
            questions: loadedQuestions,
          };

          sessionCache[testId] = session;

          setSessionType("assignment");
          setDefinition(session.definition);
          setProgress(session.progress);
          setQuestions(session.questions);
          return;
        }

        /* ======================
           DIAGNOSTIC FLOW
        ====================== */

        const diagAttemptRef = doc(
          db,
          "users",
          userUid,
          "diagnosticAttempts",
          testId
        );
        const diagAttemptSnap = await getDoc(diagAttemptRef);

        const diagDefRef = doc(db, "diagnostics", testId);
        const diagDefSnap = await getDoc(diagDefRef);

        if (!diagAttemptSnap.exists() || !diagDefSnap.exists()) {
          throw new Error("Test not found as assignment or diagnostic");
        }

        const diagDefData = diagDefSnap.data();

        // 🔑 Diagnostics also load ALL questions up front
        const questionDocs = await Promise.all(
          (diagDefData.questionIds || []).map((qid) =>
            getDoc(doc(db, "questions", qid))
          )
        );

        const loadedQuestions = questionDocs
          .filter((snap) => snap.exists())
          .map((snap) => ({
            id: snap.id,
            ...snap.data(),
          }));

        const session = {
          sessionType: "diagnostic",
          definition: { id: diagDefSnap.id, ...diagDefData },
          progress: { id: diagAttemptSnap.id, ...diagAttemptSnap.data() },
          questions: loadedQuestions,
        };

        sessionCache[testId] = session;

        setSessionType("diagnostic");
        setDefinition(session.definition);
        setProgress(session.progress);
        setQuestions(session.questions);
      } catch (err) {
        console.error("❌ Failed to load test session:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [testId, userUid]);

  return {
    sessionType,
    definition,
    progress,
    questions, // ✅ NOW DEFINED
    loading,
  };
}
